import { useEffect, useState } from "react";
import { MessageCircle, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { waLink } from "@/lib/whatsapp";

/**
 * "Send via WhatsApp" dialog — click-to-chat (wa.me).
 * Builds a WhatsApp link with the recipient + an editable, pre-filled message.
 * Staff review/edit the text, then WhatsApp (app or WhatsApp Web) opens with it
 * pre-filled and they press Send there. No API keys / Meta setup required.
 */
export function WhatsAppSendDialog({ open, onClose, to, customerName, defaultText = "", onSent }) {
  const [text, setText] = useState(defaultText);
  useEffect(() => { if (open) setText(defaultText); }, [open, defaultText]);

  const noNumber = !waLink(to, "x"); // phone missing/invalid regardless of text

  const openWhatsApp = () => {
    if (noNumber) { toast.error("No valid mobile number on file for this customer"); return; }
    if (!text.trim()) { toast.error("Enter a message"); return; }
    window.open(waLink(to, text), "_blank", "noopener,noreferrer");
    onSent?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="wa-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" />Send via WhatsApp</DialogTitle>
          <DialogDescription>
            {customerName ? `To ${customerName}` : "Review the message"}{to ? ` · ${to}` : ""}. Edit the message, then WhatsApp opens with it pre-filled — press Send there.
          </DialogDescription>
        </DialogHeader>

        {noNumber ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="wa-no-number">
            <p className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" />No valid mobile number</p>
            <p className="mt-1">Add a valid mobile number to this customer&apos;s record to send a WhatsApp message.</p>
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            data-testid="wa-message-input"
            className="w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        )}

        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary" data-testid="wa-cancel-btn">Cancel</button>
          <button onClick={openWhatsApp} disabled={noNumber} data-testid="wa-send-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
            <Send className="h-4 w-4" />Open WhatsApp
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Small green "Send WhatsApp" action button used across record screens. */
export function WhatsAppButton({ onClick, label = "Send WhatsApp", testId = "wa-open-btn", className = "" }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-lg border border-[#25D366] bg-[#25D366]/10 px-3 py-2.5 text-sm font-bold text-[#128C7E] hover:bg-[#25D366]/20 ${className}`}>
      <MessageCircle className="h-4 w-4" />{label}
    </button>
  );
}
